import type { CSSProperties } from 'react';
import {
  BookOpen,
  CircleQuestionMark,
  ClipboardList,
  Images,
  Layers,
  MapPinned,
  Palette,
  RefreshCw,
  Tags,
  Truck,
  Users,
  Video,
  WandSparkles,
  type LucideIcon,
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowsRotate,
  faBars,
  faBookOpen,
  faBuilding,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faCircleQuestion,
  faClipboardList,
  faEllipsis,
  faImages,
  faKey,
  faLanguage,
  faLayerGroup,
  faMagnifyingGlass,
  faMapLocationDot,
  faPalette,
  faPlus,
  faRightFromBracket,
  faTags,
  faTriangleExclamation,
  faTruckFast,
  faUsers,
  faVideo,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';
import type { AdminStatusTone } from './adminTokens';

/**
 * Admin icon set: one Font Awesome solid glyph per navigation destination and
 * recurring admin action. Course block icons are not part of this map.
 */
export const adminNavIcons = {
  'author-with-ai': faWandMagicSparkles,
  lessons: faBookOpen,
  courses: faLayerGroup,
  questions: faCircleQuestion,
  centerstage: faVideo,
  'image-library': faImages,
  maps: faMapLocationDot,
  tags: faTags,
  users: faUsers,
  'theme-settings': faPalette,
  'assigned-questionnaires': faClipboardList,
  'admin-republish': faArrowsRotate,
  delivery: faTruckFast,
} as const satisfies Record<string, IconDefinition>;

export type AdminNavIconId = keyof typeof adminNavIcons;

/** Outline glyphs for the global navigation rail only; page headers and actions keep the Font Awesome set. */
export const adminNavRailIcons = {
  'author-with-ai': WandSparkles,
  lessons: BookOpen,
  courses: Layers,
  questions: CircleQuestionMark,
  centerstage: Video,
  'image-library': Images,
  maps: MapPinned,
  tags: Tags,
  users: Users,
  'theme-settings': Palette,
  'assigned-questionnaires': ClipboardList,
  'admin-republish': RefreshCw,
  delivery: Truck,
} as const satisfies Record<AdminNavIconId, LucideIcon>;

/** Decorative outline glyph for the navigation rail. */
export function AdminNavRailIcon({ id, color }: { id: AdminNavIconId; color?: string }) {
  const Icon = adminNavRailIcons[id];
  return <Icon size={20} strokeWidth={1.75} aria-hidden="true" style={{ flexShrink: 0 }} color={color} />;
}

export const adminActionIcons = {
  menu: faBars,
  search: faMagnifyingGlass,
  add: faPlus,
  more: faEllipsis,
  password: faKey,
  logout: faRightFromBracket,
  language: faLanguage,
  clientContext: faBuilding,
} as const satisfies Record<string, IconDefinition>;

export const adminStatusIcons: Record<AdminStatusTone, IconDefinition> = {
  info: faCircleInfo,
  success: faCircleCheck,
  warning: faTriangleExclamation,
  error: faCircleExclamation,
};

/** Glyph sizes: navigation and actions 16 px, section markers 18 px, small inline 14 px. */
export const adminIconSize = {
  inline: 14,
  action: 16,
  section: 18,
} as const;

interface AdminIconProps {
  icon: IconDefinition;
  size?: number;
  style?: CSSProperties;
}

/**
 * Decorative admin glyph. Font Awesome renders it `aria-hidden`, so the visible
 * label (or the control's accessible name) carries the meaning.
 */
export function AdminIcon({ icon, size = adminIconSize.action, style }: AdminIconProps) {
  return <FontAwesomeIcon icon={icon} style={{ fontSize: size, flexShrink: 0, ...style }} />;
}
